import { type ReactNode } from 'react'
import spinnerIconRaw from '@/assets/spinner.svg?raw'
import styles from './Button.module.scss'

type ButtonVariant =
  | 'default'
  | 'primary'
  | 'light'
  | 'error'
  | 'warning'
  | 'success'

interface ButtonProps {
  text?: string
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  disabled?: boolean
  loading?: boolean
  onClick?: () => void
  variant?: ButtonVariant
  type?: 'button' | 'submit' | 'reset'
  rounded?: boolean
  className?: string
  testId?: string
}

export const Button = ({
  text,
  icon,
  iconPosition = 'right',
  disabled = false,
  loading = false,
  onClick,
  variant = 'default',
  type = 'button',
  rounded = false,
  className,
  testId,
}: ButtonProps) => {
  const hasText = Boolean(text)
  const hasIcon = Boolean(icon) || loading
  const iconOnly = hasIcon && !hasText

  const iconSize = hasText ? 20 : 24

  const classNames = [
    styles.button,
    styles[variant],
    iconOnly && styles.iconOnly,
    hasText && hasIcon && styles.hasGap,
    iconPosition === 'left' && styles.iconLeft,
    loading && styles.loading,
    rounded && styles.rounded,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const renderIcon = () => {
    if (loading) {
      return (
        <span
          className={styles.spinner}
          style={{ width: iconSize, height: iconSize }}
          dangerouslySetInnerHTML={{ __html: spinnerIconRaw }}
        />
      )
    }

    if (!icon) return null

    return <span className={styles.iconWrapper}>{icon}</span>
  }

  return (
    <button
      className={classNames}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      data-testid={testId}
    >
      {hasText && <span>{text}</span>}
      {renderIcon()}
    </button>
  )
}
